// Shared audio helper for vocab games.
// All games with sound should call `playWord(audio_url, fallback_text)`.
// Strategy:
//   1. Try the mp3 first.
//   2. If `playing` event doesn't fire within AUDIO_TIMEOUT_MS, OR an error
//      event fires, OR play() rejects → fall back to Web Speech API.
//   3. Otherwise the mp3 wins (and TTS is never invoked).

const AUDIO_TIMEOUT_MS = 350;

// iOS/Safari refuse speechSynthesis (and sometimes Audio.play) unless the
// page has spoken/played once INSIDE a user gesture. The auto-read moments
// (gacha reveal, auto-pull chain) run on timers, so prime both on the first
// real touch/click anywhere.
let audioUnlocked = false;
function unlockAudioOnce() {
  if (audioUnlocked || typeof window === 'undefined') return;
  audioUnlocked = true;
  try {
    const synth = window.speechSynthesis;
    if (synth) {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      synth.speak(u);
    }
  } catch (e) { /* ignore */ }
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx();
      if (ctx.state === 'suspended') ctx.resume();
      setTimeout(() => { try { ctx.close(); } catch (e) { /* ignore */ } }, 500);
    }
  } catch (e) { /* ignore */ }
}
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', unlockAudioOnce, { once: true, capture: true });
  window.addEventListener('touchend', unlockAudioOnce, { once: true, capture: true });
}

export function speakWord(text, opts = {}) {
  if (typeof window === 'undefined' || !text) return;
  const synth = window.speechSynthesis;
  if (!synth) return;
  const trySpeak = () => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = opts.lang || 'en-US';
      u.rate = opts.rate ?? 0.85;
      u.pitch = opts.pitch ?? 1.0;
      synth.speak(u);
    } catch (e) {
      // ignore
    }
  };
  // Chrome bug workaround: if we cancel + speak in the same tick the new
  // utterance is sometimes dropped. Defer a frame when cancelling.
  if (synth.speaking || synth.pending) {
    try { synth.cancel(); } catch (e) { /* ignore */ }
    setTimeout(trySpeak, 60);
  } else {
    trySpeak();
  }
}

export function playWord(audioUrl, fallbackText, opts = {}) {
  if (!audioUrl) {
    speakWord(fallbackText, opts);
    return () => {};
  }

  let settled = false;
  const fallback = () => {
    if (settled) return;
    settled = true;
    speakWord(fallbackText, opts);
  };
  const succeed = () => { settled = true; };

  let a;
  try {
    a = new Audio(audioUrl);
  } catch (e) {
    fallback();
    return () => {};
  }

  a.addEventListener('playing', succeed, { once: true });
  a.addEventListener('error', fallback, { once: true });
  // Hard timeout — if no `playing` event by now, give up on the mp3.
  const timeoutId = setTimeout(fallback, AUDIO_TIMEOUT_MS);

  try {
    const p = a.play();
    if (p && typeof p.catch === 'function') {
      p.catch(fallback);
    }
  } catch (e) {
    fallback();
  }

  // Cleanup helper — caller can invoke if component unmounts mid-play
  return () => {
    clearTimeout(timeoutId);
    try {
      a.pause();
      a.src = '';
    } catch (e) { /* ignore */ }
  };
}

// Happy ascending chime + TTS "Yummy!" — for monster correct-answer feedback.
export function playYummy(opts = {}) {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (AC) {
    let ctx;
    try { ctx = new AC(); } catch (e) { ctx = null; }
    if (ctx) {
      const now = ctx.currentTime;
      // Ascending major arpeggio: C5 → E5 → G5
      const notes = [523.25, 659.25, 783.99];
      notes.forEach((freq, i) => {
        const t = now + i * 0.09;
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, t);
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.22, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.25);
      });
      setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1200);
    }
  }
  // Cute high-pitched "Yummy!" via Web Speech, fires after chime
  setTimeout(() => {
    speakWord(opts.text || 'Yummy!', { rate: 0.88, pitch: 1.5 });
  }, 320);
}

// Sad descending tone — for wrong-answer feedback (optional).
export function playOops() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(440, now);
  osc.frequency.linearRampToValueAtTime(200, now + 0.4);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.18, now + 0.04);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.5);
  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1000);
}

// Gacha pull effect: a mechanical "clunk" + capsule rattle.
// Fired when the user pulls the gacha lever. Pure Web Audio synthesis — no asset needed.
export function playGachaPull() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;

  // 1) Lever "clunk" — quick low pitch drop, like a mechanical handle.
  {
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(220, now);
    osc.frequency.exponentialRampToValueAtTime(70, now + 0.16);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.28, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 900;
    osc.connect(gain).connect(lp).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.25);
  }

  // 2) Capsule rattle — short burst of white noise, band-passed to "shake" tone.
  {
    const bufferSize = Math.floor(ctx.sampleRate * 0.55);
    const noiseBuf = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      // Amplitude-modulated noise → gives the "rattling balls" feel.
      const env = Math.sin((i / bufferSize) * Math.PI) * (0.5 + 0.5 * Math.sin(i * 0.05));
      data[i] = (Math.random() * 2 - 1) * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2200;
    bp.Q.value = 1.2;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.08);
    gain.gain.linearRampToValueAtTime(0.08, now + 0.35);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    src.connect(bp).connect(gain).connect(ctx.destination);
    src.start(now + 0.04);
    src.stop(now + 0.7);
  }

  // 3) Happy ding when capsule pops out.
  {
    const t = now + 0.55;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, t);
    osc.frequency.exponentialRampToValueAtTime(1320, t + 0.18);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.18, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.4);
  }

  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1500);
}

// Capsule travelling from the dome down through the chute → out the slot.
// Plays a rolling/sliding texture that descends in pitch as it travels, ending
// with a soft clink when it lands. Total duration ~1.2s — matches the
// 'dropping' → 'landed' window in GachaGame.
export function playGachaRoll() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;

  // 1) Rolling texture: filtered noise, bandpass sweeps downward as ball descends.
  {
    const dur = 1.0;
    const len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    // amplitude-modulated noise → gives the "rolling clatter" feel
    for (let i = 0; i < len; i++) {
      const env = Math.sin((i / len) * Math.PI); // bell envelope
      const mod = 0.5 + 0.5 * Math.sin(i * 0.04 + Math.sin(i * 0.013) * 3);
      d[i] = (Math.random() * 2 - 1) * env * mod;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 2.5;
    // sweep filter from 1600Hz → 450Hz (sounds like descending tube)
    bp.frequency.setValueAtTime(1600, now);
    bp.frequency.exponentialRampToValueAtTime(450, now + dur);
    const gain = ctx.createGain();
    gain.gain.value = 0.18;
    src.connect(bp).connect(gain).connect(ctx.destination);
    src.start(now);
  }

  // 2) Low "rumble" — sine wave dropping in pitch, evokes weight rolling down.
  {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.exponentialRampToValueAtTime(90, now + 1.0);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.16, now + 0.1);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.7);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 1.05);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 1.1);
  }

  // 3) Bouncy "clink" when capsule lands in the tray (~1.0s in).
  {
    const t = now + 0.98;
    [880, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t + i * 0.06);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, t + i * 0.06);
      gain.gain.linearRampToValueAtTime(0.2, t + i * 0.06 + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.06 + 0.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t + i * 0.06);
      osc.stop(t + i * 0.06 + 0.22);
    });
  }

  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1500);
}

// Capsule pop + sparkle — fires when the gacha ball cracks open.
// Two-stage: a sharp "pop" (noise burst) followed by an ascending sparkle arpeggio.
export function playGachaPop() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;

  // 1) Sharp pop — short noise burst with quick decay.
  {
    const len = Math.floor(ctx.sampleRate * 0.08);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      d[i] = (Math.random() * 2 - 1) * (1 - i / len);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 600;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.35, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    src.connect(hp).connect(gain).connect(ctx.destination);
    src.start(now);
  }

  // 2) Low "boom" to give the pop weight.
  {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, now);
    osc.frequency.exponentialRampToValueAtTime(45, now + 0.18);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.35, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.3);
  }

  // 3) Ascending sparkle arpeggio — magical reveal feel.
  const notes = [659.25, 880, 1108.73, 1318.51, 1760]; // E5 A5 C#6 E6 A6
  notes.forEach((freq, i) => {
    const t = now + 0.08 + i * 0.07;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.16, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.36);
  });

  // 4) Shimmery high noise tail — like crackling firework sparks.
  {
    const len = Math.floor(ctx.sampleRate * 0.6);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      const env = Math.pow(1 - i / len, 1.6);
      d[i] = (Math.random() * 2 - 1) * env * 0.5;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 4500;
    const gain = ctx.createGain();
    gain.gain.value = 0.18;
    src.connect(hp).connect(gain).connect(ctx.destination);
    src.start(now + 0.1);
  }

  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1600);
}

// Call this from a user-gesture handler (e.g. the first "Start" click) to
// prime the speech engine so subsequent auto-plays from setTimeout/useEffect
// are not blocked by browser autoplay policy.
export function primeSpeech() {
  if (typeof window === 'undefined') return;
  const synth = window.speechSynthesis;
  if (!synth) return;
  try {
    // Empty utterance — synth registers user-gesture for later speak() calls.
    const u = new SpeechSynthesisUtterance(' ');
    u.volume = 0;
    synth.speak(u);
  } catch (e) { /* ignore */ }
}
